import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { authColors, shadows } from '../../styles/colors';
import PublicHeader from '../layout/PublicHeader';
import PublicFooter from '../layout/PublicFooter';
import LegalContentRenderer from './LegalContentRenderer';
import SEO from '../seo/SEO';

interface LegalPageTemplateProps {
  sectionKey: 'legal' | 'privacy' | 'terms';
}

/**
 * Reusable template for legal pages (Legal, Privacy, Terms)
 * Standardizes the layout and styling across all legal pages
 */
const LegalPageTemplate: React.FC<LegalPageTemplateProps> = ({ sectionKey }) => {
  const { t, tSeo } = useI18n();
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;

    const id = location.hash.slice(1);
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash]);

  // Get translations - support nested keys like 'legal.title'
  const title = t(`${sectionKey}.title`);
  const content = t(`${sectionKey}.content`);
  const lastUpdate = t(`${sectionKey}.lastUpdate`);

  // SEO always French (same URL for all UI languages)
  const seoTitle = tSeo(`${sectionKey}.title`);
  const description = tSeo(`${sectionKey}.description`);

  // SEO meta tags for each legal page
  const seoConfig = {
    legal: {
      title: `${seoTitle} - Leadsme`,
      canonical: 'https://leadsme.app/legal',
    },
    privacy: {
      title: `${seoTitle} - Leadsme`,
      canonical: 'https://leadsme.app/privacy',
    },
    terms: {
      title: `${seoTitle} - Leadsme`,
      canonical: 'https://leadsme.app/terms',
    },
  };

  const seo = {
    ...seoConfig[sectionKey],
    description,
  };

  return (
    <div className={`min-h-screen flex flex-col ${authColors.backgroundGradient}`}>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
      />
      <PublicHeader />

      {/* Content */}
      <section className="flex-1 pt-20 pb-16">
        <div
          className={`bg-white p-10 rounded-2xl ${shadows.medium} border border-gray-100 mx-auto`}
          style={{ width: '210mm', maxWidth: '100%' }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-8">{title}</h1>

          <LegalContentRenderer content={content} />

          {lastUpdate && (
            <div className="mt-12 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                <strong>Dernière mise à jour :</strong> {lastUpdate}
              </p>
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default LegalPageTemplate;